import React, { useEffect, useState } from "react"
import { ItemPicker, SelectedSortingOption } from "@tenforce/product-fields"

import { getItemAncestry } from "../../../../../apis/selectors"
import { IItemPickerField } from "../../.."
import { useAppDispatch, useAppSelector } from "../../../../../hooks"
import { GlobalState } from "../../../../../store"
import HTMLAndInternalLinkParser from "../../common/HTMLAndInternalLinkParser"
import { Labels } from "../../../../../util/Translations"
import { addErrorNotification } from "../../../../notifications/actions"
import { getItemDetailUrl } from "../../../../../util/UrlGenerator"
import env from "../../../../../env"
import {
    fetchAllConceptField,
    fetchVisibleFields,
    updateVisibleFields,
    fetchCollectionItems,
    fetchAvailableSortingFields,
} from "../../../../collection/slice"
import { SliceDataParams, Sorting } from "../../../../collection"
import { Field } from "../../Field"
import { IItem } from "../../../../../models"
import { formatString } from "../../../../../util/Strings"
import { Symbols } from "@tenforce/toolbox-fontmap"

type Props = IItemPickerField

const ItemPickerNew: React.FC<Props> = (props: Props) => {
    const dispatch = useAppDispatch()
    const [itemsWithParents, setItemsWithParents] = useState<IItem[]>([])
    const [selectedItemsIds, setSelectedItemsIds] = useState<number[]>([])
    const [isSortingLoading, setIsSortingLoading] = useState<boolean>(true)
    const {
        description,
        isSingleSelect,
        value: selectedItems,
        isDisabled,
        itemId,
        fieldId,
        title,
        odataType,
        isLoading = false,
        ...rest
    } = props

    const { isLoading: globalIsLoading, currentFieldInEditMode = "" } =
        useAppSelector((state: GlobalState) => state.fields)

    const {
        visibleFields,
        isQueryProcessing,
        visibleFieldsIds,
        selectedSorting = [],
        availableSortingOptions,
        selectableFieldsData,
        items,
    } = useAppSelector((state: GlobalState) => state.collections[fieldId] || {})

    const fetchAncestry = async (itemId: number): Promise<IItem[]> => {
        try {
            let parents = await getItemAncestry(itemId)
            if (parents.length > 0) {
                parents = parents.map((parent) => ({
                    ...parent,
                    url: getItemDetailUrl(env.applicationPath, "", parent.id),
                }))
            }
            return parents
        } catch (error) {
            dispatch(
                addErrorNotification(
                    error instanceof Error
                        ? error.message
                        : "Something went wrong when fetching ancestry.",
                ),
            )
            return []
        }
    }

    useEffect(() => {
        const fetchParentsForItems = async (): Promise<void> => {
            const updatedItems = []
            const selectedIds: number[] = []
            for (const item of selectedItems) {
                selectedIds.push(item.id)
                const parentsData = await fetchAncestry(item.id)
                updatedItems.push({
                    ...item,
                    url: getItemDetailUrl(env.applicationPath, "", item.id),
                    parents: parentsData,
                })
            }
            setSelectedItemsIds(selectedIds)
            setItemsWithParents(updatedItems)
        }
        fetchParentsForItems()
    }, [selectedItems])

    const isFieldDisabled =
        isDisabled || !["", fieldId].includes(currentFieldInEditMode)

    const fetchCollectionData = async (): Promise<void> => {
        const resultAction = await dispatch(
            fetchVisibleFields({ itemId, fieldId, odataType }),
        )
        dispatch(fetchCollectionItems({ itemId, fieldId, odataType }))
        if (fetchVisibleFields.fulfilled.match(resultAction)) {
            dispatch(
                fetchAllConceptField({ fieldId, filter: "", top: 20, skip: 0 }),
            )
        }
    }

    const fetchSortingFields = async (): Promise<void> => {
        setIsSortingLoading(true)
        await dispatch(
            fetchAvailableSortingFields({ itemId, fieldId, odataType }),
        )
        setIsSortingLoading(false)
    }

    const onSaveViewSettings = async (settingsData: {
        selectedFields?: string[]
        sorting?: SelectedSortingOption[]
    }): Promise<void> => {
        const apiPayload: SliceDataParams = {
            itemId,
            fieldId,
            odataType,
            selectedFields: settingsData.selectedFields,
            sorting:
                settingsData?.sorting?.map(
                    (option: SelectedSortingOption): Sorting => {
                        return {
                            direction: option.mode,
                            fieldDefinitionId: option.columnId,
                        }
                    },
                ) ?? [],
        }

        await dispatch(updateVisibleFields(apiPayload)).unwrap()
        fetchCollectionData()
    }

    const onFilterChange = (filter: string): void => {
        dispatch(
            fetchAllConceptField({
                fieldId,
                filter,
                top: 20,
                skip: 0,
            }),
        )
    }

    const loadMoreSelectableValue = (filter: string): void => {
        dispatch(
            fetchAllConceptField({
                fieldId,
                filter,
                top: 20,
                skip: selectableFieldsData.currentSkip,
            }),
        )
    }

    const {
        ClickHereToEdit,
        FilterAllNValues,
        FilterNSelectedValues,
        ItemPickerAddItem,
        ItemPickerAddItems,
        ItemPickerEditItem,
        ItemPickerEditItems,
        LoadNMore,
        LoadNMoreOf,
        NothingFound,
        ShowAllValues,
        ShowSelectedValues,
    } = Labels

    return (
        <ItemPicker
            {...rest}
            isDisabled={isFieldDisabled}
            description={
                <HTMLAndInternalLinkParser
                    content={(description as string) ?? ""}
                />
            }
            fieldId={fieldId}
            title={title}
            isLoading={globalIsLoading || isLoading}
            isSingleSelect={isSingleSelect}
            labels={{
                AddItem: ItemPickerAddItem,
                AddItems: ItemPickerAddItems,
                EditItem: ItemPickerEditItem,
                EditItems: ItemPickerEditItems,
                EmptyPlaceholder: ClickHereToEdit,
                FilterAllNValues: (count) =>
                    Symbols.Search + formatString(FilterAllNValues, count),
                FilterNSelectedValues: (count) =>
                    Symbols.Search + formatString(FilterNSelectedValues, count),
                FilterNoResults: NothingFound,
                LoadNMore: (remaining) => formatString(LoadNMore, remaining),
                LoadNMoreOf: (pageSize, remaining) =>
                    formatString(LoadNMoreOf, pageSize, remaining),
                ShowAllValuesTooltip: ShowAllValues,
                ShowSelectedValuesTooltip: ShowSelectedValues,
                // TODO: Add labels to platform
                NoItems: "No items.",
                OpenViewSettings: "Open view settings",
                SortLevelOne: "Sort by",
                SortLevelTwo: "Then by",
                SortLevelThree: "Then by",
                ViewSettings: "View settings",
            }}
            value={itemsWithParents}
            collection={{
                visibleFields: visibleFields,
                selectedItemIds: selectedItemsIds,
                visibleFieldsIds: visibleFieldsIds,
                isQueryProcessing: isQueryProcessing || isLoading,
                items: items,
            }}
            fieldSelection={{
                onFilterChange: onFilterChange,
                loadMoreSelectableValues: loadMoreSelectableValue,
                selectableFields: {
                    data: selectableFieldsData?.allConceptFields || [],
                    totalCount: selectableFieldsData?.totalValueCount || 0,
                    currentSkip: selectableFieldsData?.currentSkip || 0,
                    hasMore: selectableFieldsData?.hasMore || false,
                },
            }}
            sorting={{
                // TODO: Replace
                totalAvailableSortingOptions: 1000,
                isSortingLoading: isSortingLoading,
                selectedSorting: selectedSorting.map((sorting) => {
                    return {
                        columnId: sorting.fieldDefinitionId,
                        mode: sorting.direction,
                    }
                }),
                selectableSortingOptions: availableSortingOptions,
                fetchSelectableSortingOptions: fetchSortingFields,
            }}
            fetchCollectionData={fetchCollectionData}
            onSaveViewSettings={onSaveViewSettings}
            getItemField={(itemField): JSX.Element => {
                return <Field field={itemField} isLoading={false} />
            }}
        />
    )
}

export default ItemPickerNew
